import { asText, Content } from '@prismicio/client';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';
import {
  JSXMapSerializer,
  PrismicRichText,
  SliceComponentProps,
} from '@prismicio/react';
import { ArrowRightIcon } from '@/assets';

// Base typography for original (non blog) variants
const baseSerializer: JSXMapSerializer = {
  heading1: ({ children }) => (
    <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
      {children}
    </h1>
  ),
  paragraph: ({ children }) => (
    <p className="text-lg md:text-xl text-white/80 max-w-md lg:max-w-max">
      {children}
    </p>
  ),
};

export type HeroWithBulletPointsProps =
  SliceComponentProps<Content.HeroWithBulletPointsSlice>;

const HeroWithBulletPoints = ({
  slice,
}: HeroWithBulletPointsProps): JSX.Element => {
  if (!slice || !slice.primary) {
    return (
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center text-white">
            <p>Hero content is not available</p>
          </div>
        </div>
      </section>
    );
  }

  const isReversed = slice.variation === 'reversedHeroWithFeatures';
  const isWithHighlightText = slice.variation === 'withHighlightText';
  const isBlogStyling = slice.variation === 'blogStyling';

  // Dedicated blog styling variant: returns early with its own layout & typography so other pages unaffected
  if (isBlogStyling) {
    const blogComponents: JSXMapSerializer = {
      heading1: ({ children }) => (
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
          {children}
        </h1>
      ),
      paragraph: ({ children }) => (
        <p className="text-xl md:text-2xl text-white/80 max-w-md lg:max-w-max">
          {children}
        </p>
      ),
    };

    // In blogStyling variant we ignore other variation-specific behaviors
    const isReversedLocal = false;
    const isWithHighlightTextLocal = false; // blog styling uses title as h3

    const BlogTextContent = () => {
      const highlightedText = undefined;
      const plainTitleText = slice.primary.title
        ? asText(slice.primary.title)
        : '';
      return (
        <div className="flex flex-col gap-8">
          {slice.primary.title && (
            <>
              {isWithHighlightTextLocal &&
              highlightedText &&
              plainTitleText.includes(highlightedText) ? (
                <h3 className="text-4xl md:text-5xl lg:text-6xl max-w-md font-bold text-white leading-tight">
                  {plainTitleText
                    .split(new RegExp(`(${highlightedText})`, 'gi'))
                    .map((part, i) => (
                      <span key={i}>{part}</span>
                    ))}
                </h3>
              ) : (
                <PrismicRichText
                  field={slice.primary.title}
                  components={{
                    heading2: () => null,
                    heading3: ({ children }) => (
                      <h3 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight">
                        {children}
                      </h3>
                    ),
                  }}
                />
              )}
            </>
          )}
          {slice.primary.description && (
            <PrismicRichText
              field={slice.primary.description}
              components={blogComponents}
            />
          )}
          {slice.primary.primary_cta && (
            <PrismicNextLink
              field={slice.primary.primary_cta}
              className="secondary-button mt-4 max-w-max lg:max-w-[40%]"
            >
              <ArrowRightIcon className="arrow-icon w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 group-hover:mr-2 transition-all duration-300" />
              <span>{slice.primary.button_label || 'Talk to an Expert'}</span>
            </PrismicNextLink>
          )}
        </div>
      );
    };

    const BlogBulletPoints = () => (
      <div className="flex flex-col gap-7">
        {slice.primary.bullet_points &&
          Array.isArray(slice.primary.bullet_points) &&
          slice.primary.bullet_points.map((item, index) => (
            <div
              key={index}
              className="glassy-container-white rounded-2xl p-7 flex items-center gap-7"
            >
              {item.icon && (
                <PrismicNextImage
                  field={item.icon}
                  className="w-12 h-12"
                  fallbackAlt=""
                />
              )}
              {item.text && (
                <span className="text-white text-xl md:text-2xl font-semibold leading-snug">
                  {item.text}
                </span>
              )}
            </div>
          ))}
      </div>
    );

    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className={`relative py-14 md:py-40 mt-0 md:mt-0 pt-0 md:pt-32 overflow-hidden ${!slice.primary.background_color ? 'bg-custom-hero' : ''}`}
        style={{ backgroundColor: slice.primary.background_color || '' }}
      >
        <div className="w-[1440px] mx-auto pr-[174px] pl-[45px] py-8 md:py-0 relative z-0">
          <div
            className="grid md:grid-cols-2 gap-[51px] items-center"
            style={{ marginLeft: '56px', width: '1264px' }}
          >
            {isReversedLocal ? (
              <>
                <BlogBulletPoints />
                <BlogTextContent />
              </>
            ) : (
              <>
                <BlogTextContent />
                <BlogBulletPoints />
              </>
            )}
          </div>
        </div>
      </section>
    );
  }

  const TextContent = () => {
    const highlightedText = isWithHighlightText
      ? (
          slice.primary as Content.HeroWithBulletPointsSliceWithHighlightTextPrimary
        ).text_to_highlight
      : undefined;

    const plainTitleText = slice.primary.title
      ? asText(slice.primary.title)
      : '';

    const headingClasses = 'text-3xl md:text-4xl lg:text-5xl';

    return (
      <div className="flex flex-col gap-8 text-white">
        {slice.primary.title && (
          <>
            {isWithHighlightText &&
            highlightedText &&
            plainTitleText.includes(highlightedText) ? (
              <h3
                className={`${headingClasses} max-w-md font-bold text-white leading-tight`}
              >
                {plainTitleText
                  .split(new RegExp(`(${highlightedText})`, 'gi'))
                  .map((part, i) =>
                    part.toLowerCase() === highlightedText.toLowerCase() ? (
                      <span key={i} className="text-[#05F273]">
                        {part}
                      </span>
                    ) : (
                      part
                    ),
                  )}
              </h3>
            ) : (
              <PrismicRichText
                field={slice.primary.title}
                components={{
                  heading2: ({ children }) =>
                    slice.variation === 'reversedHeroWithFeatures' ? (
                      <h2
                        className={`${headingClasses} font-bold text-white leading-tight`}
                      >
                        {children}
                      </h2>
                    ) : null,
                  heading3: ({ children }) =>
                    slice.variation === 'default' ||
                    slice.variation === 'withHighlightText' ? (
                      <h3
                        className={`${headingClasses} font-bold text-white leading-tight`}
                      >
                        {children}
                      </h3>
                    ) : null,
                }}
              />
            )}
          </>
        )}

        {slice.primary.description && (
          <PrismicRichText
            field={slice.primary.description}
            components={baseSerializer}
          />
        )}
        {slice.primary.primary_cta && (
          <PrismicNextLink
            field={slice.primary.primary_cta}
            className="secondary-button mt-4 max-w-max lg:max-w-[40%]"
          >
            <ArrowRightIcon className="arrow-icon w-0 opacity-0 group-hover:w-5 group-hover:opacity-100 group-hover:mr-2 transition-all duration-300" />
            <span>{slice.primary.button_label || 'Talk to an Expert'}</span>
          </PrismicNextLink>
        )}
      </div>
    );
  };

  const BulletPoints = () => (
    <div className="flex flex-col gap-6">
      {slice.primary.bullet_points &&
        Array.isArray(slice.primary.bullet_points) &&
        slice.primary.bullet_points.map((item, index) => (
          <div
            key={index}
            className="glassy-container-white rounded-2xl p-6 flex gap-6 items-center"
          >
            {item.icon && (
              <PrismicNextImage
                field={item.icon}
                className="w-10 h-10"
                fallbackAlt=""
              />
            )}
            {item.text && (
              <span className="text-white text-lg font-semibold">
                {item.text}
              </span>
            )}
          </div>
        ))}
    </div>
  );

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={`py-10 md:py-32 mt-0 md:mt-[-5.375rem] pt-0 md:pt-28 ${!slice.primary.background_color ? 'bg-custom-hero' : ''}`}
      style={{ backgroundColor: slice.primary.background_color || '' }}
    >
      <div className="container mx-auto py-8 md:py-0 px-5 md:px-6">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {isReversed ? (
            <>
              <BulletPoints />
              <TextContent />
            </>
          ) : (
            <>
              <TextContent />
              <BulletPoints />
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroWithBulletPoints;
